import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPostBySlug, formatPostDate } from '../utils/blog';

// Verified against a real published post (hormones-across-life-stages) that
// react-helmet-async isn't actually updating the page at all here — the
// <title> in the Helmet block below never overrides the static one in
// public/index.html, in dev or in a production build. Pre-existing, not
// caused by this change — worth its own fix — but it means the schema
// script tags can't be trusted to Helmet either. Injected directly instead:
// a real <script type="application/ld+json"> appended to <head>, removed on
// unmount/change so navigating between posts can't leave a stale or
// duplicate schema block behind.
function useJsonLdSchema(articleJson, faqJson) {
  useEffect(() => {
    // Bug fix (18 Aug 2026) — prerendering (scripts/prerender.js) runs this
    // exact effect once to produce the static HTML it captures, so the
    // schema <script> tags it appends are already baked into the page
    // Puppeteer saves. Hydration in the real browser then runs this effect
    // AGAIN: since these tags are inserted directly via document.createElement
    // rather than through React's own tree, hydration has no way to
    // recognise the prerendered ones as "already there" and dedupe against
    // them, so every live post ended up with the schema twice (Article +
    // FAQPage from prerender, Article + FAQPage again from hydration).
    // Clearing anything already tagged with our own marker before adding
    // new ones makes this idempotent regardless of how many times it fires.
    document.querySelectorAll('script[data-hormonely-blog-schema]').forEach((el) => el.remove());

    const elements = [articleJson, faqJson].filter(Boolean).map((json) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.dataset.hormonelyBlogSchema = 'true';
      el.text = json;
      document.head.appendChild(el);
      return el;
    });
    return () => elements.forEach((el) => el.remove());
  }, [articleJson, faqJson]);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found

  useJsonLdSchema(post?.schemaArticle, post?.schemaFaq);

  useEffect(() => {
    let cancelled = false;
    setPost(undefined);
    getPostBySlug(slug).then((loaded) => {
      if (!cancelled) setPost(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="page-hero">
        <div className="container page-hero__inner">
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (post === null) {
    return (
      <>
        <Helmet defer={false}>
          <title>Article not found | Hormonely</title>
        </Helmet>
        <div className="page-hero">
          <div className="container page-hero__inner">
            <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>Blog</div>
            <h1 className="page-hero__title">Article not found</h1>
            <p className="page-hero__sub">We couldn't find that article. It may have been moved or removed.</p>
          </div>
        </div>
        <section className="section section--white">
          <div className="container">
            <Link to="/blog" className="btn-clay">Back to the blog</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Helmet defer={false}>
        <title>{post.title} | Hormonely</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <div className="page-hero">
        <div className="container page-hero__inner">
          <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>
            <Link to="/blog" style={{ color: 'var(--clay-lt)' }}>Blog</Link>
          </div>
          <h1 className="page-hero__title">{post.title}</h1>
          <p className="page-hero__sub">{formatPostDate(post.date)}</p>
        </div>
      </div>

      <section className="section section--white">
        <div className="container">
          <div className="prose">
            <div className="disclaimer-box">
              This article provides information only. It is not medical advice. Always speak to your GP or a qualified health professional before making changes to your health, starting supplements, or pursuing hormone treatment.
            </div>
            <div dangerouslySetInnerHTML={{ __html: post.html }} />
            <div style={{ marginTop: '32px' }}>
              <Link to="/blog" className="btn-outline">← Back to the blog</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
